import { Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export const normalizeQuickSearch = (value: unknown) => String(value ?? "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/đ/g, "d")
  .replace(/Đ/g, "D")
  .toLocaleLowerCase("vi-VN")
  .trim();

export const matchesQuickSearch = (query: string, ...values: unknown[]) => {
  const term = normalizeQuickSearch(query);
  return !term || normalizeQuickSearch(values.join(" ")).includes(term);
};

export function QuickSearch({
  value,
  onChange,
  placeholder = "Tìm theo số, mã, tên hoặc nội dung...",
  resultCount,
  scope = "closest-panel"
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  resultCount?: number;
  scope?: "closest-panel" | "next";
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(resultCount);

  useEffect(() => {
    const root = rootRef.current;
    const target = scope === "next"
      ? root?.nextElementSibling
      : root?.closest(".module-panel, .full-panel, [data-search-scope]");
    if (!target) return;

    const applyFilter = () => {
      const candidates = Array.from(target.querySelectorAll<HTMLElement>(
        "table.data-table > tbody > tr, .account-card, .report-summary-card, .accounting-summary-report > button"
      )).filter((candidate) => !candidate.matches("tr") || !candidate.querySelector("td[colspan]"));
      let count = 0;
      candidates.forEach((candidate) => {
        const visible = matchesQuickSearch(value, candidate.textContent);
        candidate.style.display = visible ? "" : "none";
        if (visible) count += 1;
      });
      setVisibleCount(count || (!value ? resultCount : 0));
    };

    applyFilter();
    const observer = new MutationObserver(applyFilter);
    observer.observe(target, { childList: true, subtree: true, characterData: true });
    return () => {
      observer.disconnect();
      target.querySelectorAll<HTMLElement>("table.data-table > tbody > tr, .account-card, .report-summary-card, .accounting-summary-report > button")
        .forEach((candidate) => { candidate.style.display = ""; });
    };
  }, [resultCount, scope, value]);

  return (
    <div className="quick-search-toolbar" ref={rootRef}>
      <label className="search-box quick-search-box">
        <Search size={17} />
        <input
          type="search"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          aria-label="Tìm kiếm nhanh trong danh sách"
          autoComplete="off"
        />
        {value && <button type="button" onClick={() => onChange("")} title="Xóa nội dung tìm kiếm" aria-label="Xóa nội dung tìm kiếm"><X size={15} /></button>}
      </label>
      {(visibleCount !== undefined || resultCount !== undefined) && <span>{visibleCount ?? resultCount} kết quả</span>}
    </div>
  );
}
